import dayjs from 'dayjs';

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { getClientTimezoneValue } from '../../../helpers/timezone/TimezoneHelper';
import { FrequencyDto } from '../../../interfaces/ScheduledJobConfiguration';
import { MONTH_OPTIONS } from '../SchedulingSections.constants';

dayjs.extend(utc);
dayjs.extend(timezone);

const USER_TIMEZONE_VALUE = getClientTimezoneValue();

const convertWeekdayToJS = (weekday: number): number => (weekday === 7 ? 0 : weekday);

const getLastDayOfMonth = (year: number, month: number): number =>
	dayjs(`${year}-${String(month + 1).padStart(2, '0')}-01`)
		.endOf('month')
		.date();

const getTargetDayForMonth = (day: number, year: number, month: number): number | null => {
	if (day === -1) {
		return getLastDayOfMonth(year, month);
	}
	const lastDay = getLastDayOfMonth(year, month);
	return day > lastDay ? null : day;
};

const createDateAtTime = (base: dayjs.Dayjs, hours: number, minutes: number): dayjs.Dayjs =>
	base.hour(hours).minute(minutes).second(0).millisecond(0);

const calculateNextWeeklyScheduledDate = (
	wdays: number[],
	interval: number,
	hours: number,
	minutes: number,
	timezone: string
): Date | null => {
	if (!wdays?.length) {
		return null;
	}

	const now = dayjs().tz(timezone);
	const selectedWeekdays = wdays.map(convertWeekdayToJS);
	const currentWeekday = now.day();

	const sortedWeekdays = [...selectedWeekdays].sort((a, b) => {
		const daysA = (a - currentWeekday + 7) % 7 || 7;
		const daysB = (b - currentWeekday + 7) % 7 || 7;
		return daysA - daysB;
	});

	for (let weekMultiplier = 0; weekMultiplier < 8; weekMultiplier++) {
		const weekOffset = weekMultiplier * interval;
		for (const weekday of sortedWeekdays) {
			const daysUntilWeekday = (weekday - currentWeekday + 7) % 7;
			const totalDays = daysUntilWeekday + weekOffset * 7;
			const targetDate = createDateAtTime(now.add(totalDays, 'day'), hours, minutes);

			if (totalDays === 0 && targetDate.isBefore(now)) {
				continue;
			}
			if (targetDate.isAfter(now)) {
				return targetDate.toDate();
			}
		}
	}

	// Fallback
	const daysUntilFirstWeekday = (sortedWeekdays[0] - currentWeekday + 7) % 7 || 7;
	return createDateAtTime(
		now.add(daysUntilFirstWeekday + interval * 7, 'day'),
		hours,
		minutes
	).toDate();
};

const calculateNextMonthlyScheduledDate = (
	mdays: number[],
	months: number[] | undefined,
	interval: number,
	hours: number,
	minutes: number,
	timezone: string
): Date | null => {
	if (!mdays?.length) {
		return null;
	}

	const now = dayjs().tz(timezone);
	// When no specific months selected, consider all months
	const selectedMonths = months?.length ? months : MONTH_OPTIONS.map((m) => m.value as number);

	for (let monthOffset = 0; monthOffset < 24 * interval; monthOffset++) {
		const candidateDate = now.add(monthOffset, 'month');
		const candidateYear = candidateDate.year();
		const candidateMonth = candidateDate.month();

		// Check if this month is in the selected months (1-indexed)
		if (!selectedMonths.includes(candidateMonth + 1)) {
			continue;
		}

		// Check interval constraint
		if (monthOffset % interval !== 0) {
			continue;
		}

		for (const day of mdays) {
			const targetDay = getTargetDayForMonth(day, candidateYear, candidateMonth);
			if (targetDay === null) {
				continue;
			}

			const dateStr = `${candidateYear}-${String(candidateMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
			const targetDate = dayjs.tz(dateStr, timezone).second(0).millisecond(0);

			if (targetDate.isAfter(now)) {
				return targetDate.toDate();
			}
		}
	}

	// Fallback
	const futureDate = now.add(interval, 'month');
	const firstDay =
		mdays[0] === -1 ? getLastDayOfMonth(futureDate.year(), futureDate.month()) : mdays[0];
	return createDateAtTime(futureDate.date(firstDay), hours, minutes).toDate();
};

export const calculateNextScheduledDate = (config: {
	frequency?: string;
	interval?: number;
	hours?: number[];
	minutes?: number[];
	timezone?: string;
	wdays?: number[];
	mdays?: number[];
	months?: number[];
	lastDayOfMonth?: boolean;
}): Date | null => {
	if (!config.frequency) {
		return null;
	}

	let hours: number;
	let minutes: number;

	if (config.hours?.[0] !== undefined && config.minutes?.[0] !== undefined) {
		hours = config.hours[0];
		minutes = config.minutes[0];
	} else if (config.minutes?.[0] !== undefined) {
		const totalMinutes = config.minutes[0];
		hours = Math.floor(totalMinutes / 60);
		minutes = totalMinutes % 60;
	} else {
		return null;
	}

	const timezone = config.timezone || USER_TIMEZONE_VALUE;
	const interval = config.interval || 1;

	if (
		config.frequency === FrequencyDto.WEEKLY ||
		config.frequency === FrequencyDto.EVERY_OTHER_WEEK
	) {
		return calculateNextWeeklyScheduledDate(config.wdays || [], interval, hours, minutes, timezone);
	}

	if (config.frequency === FrequencyDto.MONTHLY) {
		// Handle lastDayOfMonth boolean: if true and mdays doesn't include -1, add it
		let mdays = config.mdays || [];
		if (config.lastDayOfMonth && !mdays.includes(-1)) {
			mdays = [...mdays, -1];
		}

		return calculateNextMonthlyScheduledDate(
			mdays,
			config.months,
			interval,
			hours,
			minutes,
			timezone
		);
	}

	return null;
};
